import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable } from 'rxjs';
import { ICON } from '@shared/constants/icons.constant';
import { SearchConnector, SearchResultItem, SearchScope } from '@shared/global-search';
import { Icon } from '@shared/types/icon.type';
import { EmailTask } from '../../../shared/models/entity/email-task/email-task.model';
import { EmailTaskFilter } from '../../../shared/models/entity/email-task/email-task-filter.model';
import { EmailTasksService } from '../../../shared/services/entity/email-tasks.service';

const MAX_RESULTS = 6;

/**
 * Searches the tasks of the board by title / description through the entity list filter.
 */
@Injectable({ providedIn: 'root' })
export class TasksSearchConnector implements SearchConnector {
  // -----------------------------------------------------------------------------------------------------
  // @ DEPENDENCIES
  // -----------------------------------------------------------------------------------------------------

  private readonly entityService = inject(EmailTasksService);

  private readonly router = inject(Router);

  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly id = 'tasks';

  readonly label = 'Aufgaben';

  readonly order = 21;

  readonly icon: Icon = ICON.checkSquare;

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  search(query: string, scope: SearchScope): Observable<SearchResultItem[]> {
    // Tasks honor every entity scope — the list filter carries all four anchors
    const context = scope.context ?? {};

    return this.entityService
      .getAll({
        filter: {
          filter: query,
          propertyId: context['propertyId'] ? String(context['propertyId']) : undefined,
          unitId: context['unitId'] ? String(context['unitId']) : undefined,
          contactId: context['contactId'] ? String(context['contactId']) : undefined,
          contractorId: context['contractorId'] ? String(context['contractorId']) : undefined,
        } as EmailTaskFilter,
        pagination: { page: 1, pageSize: MAX_RESULTS },
      })
      .pipe(map((tasks) => tasks.map((task) => this.toResultItem(task))));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  private toResultItem(task: EmailTask): SearchResultItem {
    return {
      id: task.id,
      title: task.title,
      subtitle: task.propertyName || task.suggestedAssignee,
      icon: this.icon,
      payload: task,
      actions: [
        {
          id: 'open-board',
          label: 'Zum Board',
          icon: ICON.caretRight,
          execute: () => {
            this.router.navigate(['/tasks']);
          },
        },
        {
          id: 'open-email',
          label: 'E-Mail öffnen',
          icon: ICON.envelopeOpen,
          execute: () => {
            if (task.emailId) {
              this.router.navigate(['/emails', task.emailId]);
            }
          },
        },
      ],
    };
  }
}